/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { SEO } from "@/components/SEO";
import { Section, SpecLabel } from "@/components/Section";
import { BookACallButton } from "@/components/BookACallButton";
import { Reveal } from "@/components/Reveal";

const VALUES = [
    {
        label: "Direct",
        title: "We tell you the truth.",
        desc: "If something isn't going to work, we say so. If a system needs more time, we say so. No padded reports, no jargon, no scapegoats.",
    },
    {
        label: "Systems > Services",
        title: "We install. We don't bill hours.",
        desc: "You don't pay us to think about your marketing. You pay us to install systems that actually run — even when we're sleeping.",
    },
    {
        label: "Transparent",
        title: "One price. One plan. Public.",
        desc: "$297/mo on the homepage, on the pricing page, on the call. We don't 'send over a custom quote.' Everyone sees the same number.",
    },
    {
        label: "Owner-First",
        title: "Built for the person signing the check.",
        desc: "We're not here to impress a marketing director. We're here to make the phone ring and the inbox stay clean for the person actually running the business.",
    },
];

export default function AboutPage() {
    return (
        <>
            <SEO
                title="About Elevara Systems — Built for Home Service Owners"
                description="Why Elevara Systems exists, how we operate, and who we're built for. Marketing systems for home service businesses tired of being sold bull* by agencies."
                path="/about"
            />

            {/* HERO */}
            <section
                className="relative overflow-hidden bg-white"
                data-testid="about-hero"
            >
                <div
                    className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none"
                    aria-hidden="true"
                />
                <div
                    className="absolute inset-0 hero-edge-glow pointer-events-none"
                    aria-hidden="true"
                />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
                    <SpecLabel>About</SpecLabel>
                    <h1 className="mt-4 font-display font-extrabold tracking-tight text-[#0a0b0d] text-4xl sm:text-5xl lg:text-6xl leading-[1.04] max-w-3xl">
                        Built for the people who actually
                        <br />
                        <span className="text-[#188bf6]">show up in trucks.</span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-[#4a5562] max-w-2xl leading-relaxed">
                        Elevara Systems exists because most marketing
                        companies treat home service businesses like an
                        afterthought. Wrong. You're the backbone of every
                        neighborhood. The systems we build are designed
                        around how you actually work.
                    </p>
                </div>
            </section>

            {/* STORY + FOUNDER */}
            <Section id="story" testId="about-story-section" tone="surface">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start scroll-mt-24">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <SpecLabel>Our Story</SpecLabel>
                        <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0a0b0d]">
                            Founded out of frustration, not opportunity.
                        </h2>
                        <div className="mt-6 space-y-4 text-base sm:text-lg text-[#4a5562] leading-relaxed">
                            <p>
                                Elevara Systems started after watching home
                                service operators get sold the same
                                expensive nonsense over and over: bloated
                                retainers, contracts they couldn't get out
                                of, dashboards that didn't translate to
                                booked jobs.
                            </p>
                            <p>
                                So we built the opposite. One transparent
                                price. No contract. Seven systems that
                                handle the marketing work a home service
                                business actually needs — not what an
                                agency wants to bill for.
                            </p>
                            <p>
                                Every system on this site has been built
                                because a real operator needed it and
                                couldn't find it priced or packaged like
                                this anywhere else. That's the whole pitch.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-5 order-1 lg:order-2" id="founder">
                        <div className="relative rounded-2xl overflow-hidden border-2 border-[#0a0b0d]/8 bg-white shadow-[0_24px_60px_-30px_rgba(10,11,13,0.2)]">
                            <div className="h-1.5 bg-[#188bf6]" aria-hidden="true" />
                            <img
                                src="/assets/founder.png"
                                alt="Founder of Elevara Systems"
                                className="w-full h-auto object-cover"
                                loading="lazy"
                            />
                            <div className="p-5 border-t border-[#0a0b0d]/8">
                                <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#4a5562]">
                                    The Founder
                                </div>
                                <p className="mt-2 text-sm text-[#0a0b0d] font-display font-medium">
                                    Built Elevara Systems to give home
                                    service operators a no-bull*
                                    alternative to legacy marketing
                                    agencies.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* VALUES */}
            <Section id="values" testId="about-values">
                <div className="max-w-3xl scroll-mt-24">
                    <SpecLabel>How we operate</SpecLabel>
                    <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-[#0a0b0d]">
                        Four rules. We don't break them.
                    </h2>
                </div>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {VALUES.map((v, i) => (
                        <Reveal key={v.label} delay={i * 0.05}>
                            <div
                                data-testid={`about-value-${v.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="relative h-full rounded-2xl border-2 border-[#0a0b0d]/8 bg-white p-6 sm:p-7 hover:border-[#188bf6]/40 transition-colors"
                            >
                                <div className="text-[11px] font-display font-bold uppercase tracking-[0.18em] text-[#188bf6]">
                                    {v.label}
                                </div>
                                <h3 className="mt-3 font-display text-xl font-extrabold text-[#0a0b0d]">
                                    {v.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#4a5562] leading-relaxed">
                                    {v.desc}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Section>

            {/* MISSION + CTA */}
            <section
                id="mission"
                className="relative bg-[#0a0b0d] text-white"
                data-testid="about-mission"
            >
                <div className="h-1.5 caution-stripe" aria-hidden="true" />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 scroll-mt-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-8">
                            <SpecLabel tone="dark">The mission</SpecLabel>
                            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                                Give every home service operator
                                <br />
                                <span className="text-[#5BA9F8]">
                                    marketing systems that actually work.
                                </span>
                            </h2>
                            <p className="mt-5 text-base sm:text-lg text-white/72 max-w-xl leading-relaxed">
                                Not promises. Not creative briefs. Not
                                analytics dashboards. Systems. That run.
                                Every day. Whether you log in or not.
                            </p>
                            <div className="mt-8">
                                <BookACallButton
                                    size="lg"
                                    testId="about-final-cta-button"
                                />
                            </div>
                        </div>
                        <div className="lg:col-span-4 hidden lg:flex justify-end">
                            <img
                                src="/assets/mascot_pose3.png"
                                alt=""
                                className="w-[280px] h-auto"
                                loading="lazy"
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
